import { SxProps, Typography } from "@mui/material";
import React, { ChangeEvent, FocusEvent, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Input } from "../../common/components";
import { palette, spacing } from "../../styles/theme";
import { Size } from "../../types";

interface Props {
  itemSize: Size;
  disabled?: boolean;
  greyedOutInputColor?: boolean;
  onSizeChange?: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    size: Size
  ) => void;
  sx?: SxProps;
}

const SizeInput = ({
  itemSize,
  onSizeChange,
  disabled,
  greyedOutInputColor,
  sx,
}: Props) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);

  const getDisplayValue = () => {
    if (isFocused && itemSize.qty === 0) {
      return "";
    }
    return itemSize.qty;
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);

    if (e.target.value === "" || e.target.value === "0") {
      const newSize = { ...itemSize, qty: 0 };
      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: "0" },
      } as ChangeEvent<HTMLInputElement>;
      onSizeChange?.(syntheticEvent, newSize);
    }
  };

  return (
    <Box
      display={"flex"}
      alignItems={"center"}
      justifyItems={"center"}
      flexDirection={"column"}
      maxWidth={spacing.xlMobile}
      data-test={itemSize.name}
      minWidth={"fit-content"}
      sx={sx}
    >
      <Typography
        variant="textRegularBold"
        whiteSpace={"nowrap"}
        minWidth={"fit-content"}
      >
        {t(`sizes.${itemSize.name}`)}
      </Typography>
      <Input
        containerSx={{ alignItems: "center" }}
        name={itemSize.name}
        inputProps={{
          sx: {
            textAlign: "center",
            color:
              itemSize.qty === 0 || greyedOutInputColor
                ? palette.grey.darkerGrey
                : undefined,
          },
        }}
        sx={{
          background: "white !important",
          maxWidth: spacing.xlMobile,
          px: "4px",
        }}
        value={getDisplayValue()}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={(e) => {
          let qty = e.target.value;
          if (qty === "") {
            onSizeChange?.(e, { ...itemSize, qty: 0 });
            return;
          }
          onSizeChange?.(e, { ...itemSize, qty: parseInt(qty) || 0 });
        }}
        disabled={disabled}
      />
    </Box>
  );
};

export default memo(SizeInput);
